/**
 * Live data sync utilities.
 *
 * Provides:
 *   - useLiveQuery(key, fetcher, options)  → auto-refetches on mount, interval,
 *                                            window focus, and cross-tab invalidate broadcasts.
 *   - invalidate(key | key[])              → broadcast a refresh signal for one or more keys.
 *
 * Design: uses window.storage events + a per-tab custom-event bus so both same-tab
 * and cross-tab invalidation work. No external state library required.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const CHANNEL_PREFIX = "otopark:invalidate:";
const EVENT_NAME = "otopark:invalidate";

/** Broadcast that data with these keys should be refetched across the app.
 *  Accepts a string or an array of strings. */
export function invalidate(keys) {
  const list = Array.isArray(keys) ? keys : [keys];
  const ts = Date.now();
  for (const k of list) {
    try {
      // Same-tab event
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { key: k, ts } }));
      // Cross-tab via localStorage change event
      localStorage.setItem(CHANNEL_PREFIX + k, String(ts));
    } catch (_e) { /* ignore */ }
  }
}

/**
 * Hook to fetch and keep data live.
 *
 * @param {string|string[]} keys - tag(s) this query is interested in
 * @param {() => Promise<any>} fetcher - async fetcher (called on refresh)
 * @param {object} options
 *   - intervalMs: default 20000 (0 to disable interval)
 *   - refetchOnFocus: default true
 *   - deps: extra deps that also trigger refetch (like route params)
 * @returns { data, loading, error, refetch }
 */
export function useLiveQuery(keys, fetcher, options = {}) {
  const {
    intervalMs = 20000,
    refetchOnFocus = true,
    deps = [],
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(async () => {
    try {
      const result = await fetcherRef.current();
      setData(result);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mount + deps refetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, deps);

  // Interval
  useEffect(() => {
    if (!intervalMs) return;
    const iv = setInterval(refetch, intervalMs);
    return () => clearInterval(iv);
  }, [refetch, intervalMs]);

  // Focus
  useEffect(() => {
    if (!refetchOnFocus) return;
    const onFocus = () => refetch();
    const onVis = () => { if (!document.hidden) refetch(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refetch, refetchOnFocus]);

  // Invalidate: listen for same-tab custom events & storage events
  useEffect(() => {
    const watchKeys = new Set(Array.isArray(keys) ? keys : [keys]);
    const onCustom = (e) => {
      if (e.detail?.key && watchKeys.has(e.detail.key)) refetch();
    };
    const onStorage = (e) => {
      if (!e.key || !e.key.startsWith(CHANNEL_PREFIX)) return;
      const k = e.key.slice(CHANNEL_PREFIX.length);
      if (watchKeys.has(k)) refetch();
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, JSON.stringify(Array.isArray(keys) ? keys : [keys])]);

  return { data, loading, error, refetch };
}

/**
 * Common invalidation tags used across the app.
 * Use strings from here to avoid typos:
 *   invalidate(TAGS.PLANS)
 */
export const TAGS = {
  PLANS: "plans",
  PLATE_SUBSCRIPTIONS: "plate_subscriptions",
  SUBSCRIPTION_PAYMENTS: "subscription_payments",
  SESSIONS: "sessions",
  UNPAID: "unpaid",
  KASA: "kasa",
  PAYMENTS_LEDGER: "payments_ledger",
  TARIFFS: "tariffs",
  ACCESS: "access_list",
  USERS: "users",
  COUPONS: "coupons",
  CORPORATE: "corporate",
  UNMATCHED: "unmatched_exits",
  SETTINGS: "settings",
  CAMERAS: "cameras",
  PARKING_LOTS: "parking_lots",
  DASHBOARD: "dashboard",
  LIVE_ACTIVITY: "live_activity",
  BANK_TRANSFERS: "bank_transfers",
};
